import React, { useState, useEffect } from 'react';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import { configurationApi } from '../api/configuration.api';

const MOIS_NOMS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

type TypeCalcul = 'MONTANT_FIXE' | 'TAUX_SALAIRE';

interface TaxeTemporaire {
  idTaxe: string;
  code: string;
  nom: string;
  description?: string;
  anneeDebut: number;
  anneeFin?: number;
  typeCalcul: TypeCalcul;
  montantFixe?: number | string;
  taux?: number | string;
  plafond?: number | string;
  sourceLegale: string;
  numeroDecret?: string;
  actif: boolean;
  moisApplication: { mois: number }[];
}

const FORM_VIDE = {
  code: '',
  nom: '',
  description: '',
  anneeDebut: new Date().getFullYear(),
  anneeFin: '',
  typeCalcul: 'MONTANT_FIXE' as TypeCalcul,
  montantFixe: '',
  taux: '',
  plafond: '',
  sourceLegale: '',
  numeroDecret: '',
  moisApplication: [] as number[],
};

export function TaxesTemporairesPage() {
  const [taxes, setTaxes] = useState<TaxeTemporaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(FORM_VIDE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charger = async () => {
    try {
      const data = await configurationApi.listerTaxes();
      setTaxes(Array.isArray(data) ? data : (data.donnees ?? []));
    } catch {
      setError('Erreur lors du chargement des taxes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const toggleMois = (mois: number) => {
    setForm(f => ({
      ...f,
      moisApplication: f.moisApplication.includes(mois)
        ? f.moisApplication.filter(m => m !== mois)
        : [...f.moisApplication, mois],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await configurationApi.creerTaxe({
        ...form,
        anneeDebut: Number(form.anneeDebut),
        anneeFin: form.anneeFin ? Number(form.anneeFin) : undefined,
        montantFixe: form.montantFixe ? Number(form.montantFixe) : undefined,
        taux: form.taux ? Number(form.taux) : undefined,
        plafond: form.plafond ? Number(form.plafond) : undefined,
      });
      setForm(FORM_VIDE);
      setShowForm(false);
      await charger();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const desactiver = async (idTaxe: string) => {
    if (!confirm('Désactiver cette taxe ? Elle ne sera plus appliquée aux prochains bulletins.')) return;
    try {
      await configurationApi.desactiverTaxe(idTaxe);
      await charger();
    } catch {
      setError('Erreur lors de la désactivation');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">Taxes temporaires</h1>
          <p className="text-sm text-content-subtle mt-1">
            Taxes spéciales calculées automatiquement lors de la génération des bulletins
          </p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setError(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-content-inverted rounded-lg hover:bg-primary-hover text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouvelle taxe
        </button>
      </div>

      {error && (
        <div className="bg-danger-light border border-danger rounded-lg p-4 text-sm text-danger flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Formulaire inline */}
      {showForm && (
        <div className="bg-surface rounded-xl border border-border p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-content mb-5">Nouvelle taxe temporaire</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Code *</label>
                <input
                  required
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                  placeholder="ex: CONTRIB_SPECIALE_2026"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Nom *</label>
                <input
                  required
                  value={form.nom}
                  onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Contribution spéciale 2026"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Description</label>
              <input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Type de calcul *</label>
                <select
                  value={form.typeCalcul}
                  onChange={e => setForm(f => ({ ...f, typeCalcul: e.target.value as TypeCalcul, montantFixe: '', taux: '' }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                >
                  <option value="MONTANT_FIXE">Montant fixe</option>
                  <option value="TAUX_SALAIRE">Taux sur salaire</option>
                </select>
              </div>
              {form.typeCalcul === 'MONTANT_FIXE' ? (
                <div>
                  <label className="block text-sm font-medium text-content-muted mb-1">Montant (F CFA) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={form.montantFixe}
                    onChange={e => setForm(f => ({ ...f, montantFixe: e.target.value }))}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-content-muted mb-1">Taux (0–1) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="1"
                    step="0.001"
                    placeholder="0.02"
                    value={form.taux}
                    onChange={e => setForm(f => ({ ...f, taux: e.target.value }))}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Plafond (F CFA)</label>
                <input
                  type="number"
                  min="0"
                  value={form.plafond}
                  onChange={e => setForm(f => ({ ...f, plafond: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Année début *</label>
                <input
                  type="number"
                  required
                  value={form.anneeDebut}
                  onChange={e => setForm(f => ({ ...f, anneeDebut: Number(e.target.value) }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Année fin</label>
                <input
                  type="number"
                  value={form.anneeFin}
                  onChange={e => setForm(f => ({ ...f, anneeFin: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">
                Mois d'application *
                {form.moisApplication.length === 0 && (
                  <span className="ml-2 text-danger font-normal text-xs">Sélectionnez au moins un mois</span>
                )}
              </label>
              <div className="flex flex-wrap gap-2">
                {MOIS_NOMS.map((nom, i) => {
                  const mois = i + 1;
                  const actif = form.moisApplication.includes(mois);
                  return (
                    <button
                      key={mois}
                      type="button"
                      onClick={() => toggleMois(mois)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        actif
                          ? 'bg-primary text-content-inverted shadow-sm'
                          : 'bg-surface-overlay text-content-muted hover:bg-border'
                      }`}
                    >
                      {nom}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Source légale *</label>
                <input
                  required
                  value={form.sourceLegale}
                  onChange={e => setForm(f => ({ ...f, sourceLegale: e.target.value }))}
                  placeholder="Loi n°2026-001"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Numéro décret</label>
                <input
                  value={form.numeroDecret}
                  onChange={e => setForm(f => ({ ...f, numeroDecret: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2 border-t border-border">
              <button
                type="submit"
                disabled={saving || form.moisApplication.length === 0}
                className="px-4 py-2 bg-primary text-content-inverted rounded-lg hover:bg-primary-hover text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {saving ? 'Création...' : 'Créer la taxe'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setForm(FORM_VIDE); setError(null); }}
                className="px-4 py-2 bg-surface-overlay text-content-muted rounded-lg hover:bg-border text-sm font-medium transition-colors"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tableau des taxes */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-10 text-center text-content-subtle text-sm">Chargement...</div>
        ) : taxes.length === 0 ? (
          <div className="p-10 text-center text-content-subtle">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-content-subtle" />
            <p className="font-medium">Aucune taxe temporaire active</p>
            <p className="text-xs mt-1 text-content-subtle">Créez votre première taxe avec le bouton ci-dessus</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-raised border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Code / Nom</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Montant / Taux</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Mois d'application</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Années</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {taxes.map(t => (
                <tr key={t.idTaxe} className="hover:bg-surface-raised transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-content">{t.nom}</p>
                    <p className="text-xs text-content-subtle font-mono mt-0.5">{t.code}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      t.typeCalcul === 'MONTANT_FIXE'
                        ? 'bg-accent-light text-accent'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {t.typeCalcul === 'MONTANT_FIXE' ? 'Montant fixe' : 'Taux salaire'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-content-muted">
                    {t.typeCalcul === 'MONTANT_FIXE'
                      ? `${Number(t.montantFixe ?? 0).toLocaleString('fr-FR')} F`
                      : `${(Number(t.taux ?? 0) * 100).toFixed(2)} %`}
                    {t.plafond ? (
                      <span className="text-xs text-content-subtle ml-1">
                        (plaf. {Number(t.plafond).toLocaleString('fr-FR')} F)
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {t.moisApplication
                        .sort((a, b) => a.mois - b.mois)
                        .map(m => (
                          <span
                            key={m.mois}
                            className="px-1.5 py-0.5 bg-surface-overlay text-content-muted rounded text-xs"
                          >
                            {MOIS_NOMS[m.mois - 1]}
                          </span>
                        ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-content-muted text-sm whitespace-nowrap">
                    {t.anneeDebut}{t.anneeFin ? ` → ${t.anneeFin}` : ' →'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => desactiver(t.idTaxe)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-danger hover:bg-danger-light rounded-lg text-xs font-medium transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Désactiver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
